/**
 * Reach → Leads — slice 70. The people Reach's ads brought in, as people.
 *
 * ## WHERE A LEAD LIVES
 *
 * A click-to-WhatsApp ad that makes somebody write to the workspace from a
 * number no person owns yet produces a `contacts` row in the workspace's SYSTEM
 * dataset — named {@link REACH_LEADS_DATASET_NAME}, recognised by
 * `source_filename = `{@link REACH_LEADS_DATASET_SOURCE}, created on first use
 * and unique per workspace (`0053_reach_leads.sql`). So a lead is resolved to a
 * person by the same trigger as any upload, appears in the field catalogue with
 * its ad provenance as ordinary columns ({@link REACH_LEAD_EXTRAS_KEYS}), can be
 * matched by a rule ("came from ad X"), and can start a flow
 * (`flow_trigger_events.reach_lead`).
 *
 * It is NOT an upload: every reader that lists or counts uploads excludes that
 * source name. A person who already exists only records the event — a second
 * contact row would claim a second source of a known person.
 *
 * ## WHAT IS NOT HERE
 *
 * Meta Lead Ads form submissions need a Graph call to fetch their fields. That
 * runs behind a port whose non-production implementation is a stub, and is the
 * next step rather than this contract's; a lead from a form arrives through the
 * CSV export path ({@link REACH_LEAD_CHANNEL_VALUES} `csv_import`).
 *
 * Masked on the wire, always: a lead is listed by name and one masked address.
 */
import { z } from 'zod';

import { isoDateTimeSchema, paginated, paginationQuerySchema, uuidSchema } from '../common.js';
import { maskedPersonSchema } from './segment.js';

export const REACH_LEADS_DATASET_NAME = 'Reach leads';
/** The reserved `datasets.source_filename` of the system dataset. */
export const REACH_LEADS_DATASET_SOURCE = 'reach:leads';

/** The `extras` keys a lead row carries — ordinary columns to the catalogue. */
export const REACH_LEAD_EXTRAS_KEYS = ['ad_id', 'ad_headline', 'source_url', 'lead_created_at', 'channel'] as const;
export type ReachLeadExtrasKey = (typeof REACH_LEAD_EXTRAS_KEYS)[number];

export const REACH_LEAD_CHANNEL_VALUES = ['click_to_whatsapp', 'lead_ad', 'csv_import'] as const;
export const reachLeadChannelSchema = z.enum(REACH_LEAD_CHANNEL_VALUES);
export type ReachLeadChannel = z.infer<typeof reachLeadChannelSchema>;

export const REACH_LEAD_CHANNEL_LABEL: Readonly<Record<ReachLeadChannel, string>> = {
  click_to_whatsapp: 'Click-to-WhatsApp ad',
  lead_ad: 'Lead form',
  csv_import: 'Imported from Meta’s lead export',
};

/** `GET /reach/leads` — newest first, optionally one ad's. */
export const reachLeadsQuerySchema = paginationQuerySchema
  .extend({ adId: z.string().trim().min(1).max(64).optional() })
  .strict();
export type ReachLeadsQuery = z.infer<typeof reachLeadsQuerySchema>;

export const reachLeadSchema = z
  .object({
    contactId: uuidSchema,
    person: maskedPersonSchema,
    adId: z.string().nullable(),
    adHeadline: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    channel: reachLeadChannelSchema,
    /** When the lead arrived (`lead_created_at`, else the row's creation). */
    createdAt: isoDateTimeSchema,
    /** Whether the person is walking a live flow right now. */
    inActiveFlow: z.boolean(),
  })
  .strict();
export type ReachLead = z.infer<typeof reachLeadSchema>;

export const reachLeadsByAdSchema = z
  .object({
    adId: z.string().nullable(),
    adHeadline: z.string().nullable(),
    leads: z.number().int().min(0),
    lastSevenDays: z.number().int().min(0),
    latestAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type ReachLeadsByAd = z.infer<typeof reachLeadsByAdSchema>;

export const reachLeadsResponseSchema = paginated(reachLeadSchema)
  .extend({
    byAd: z.array(reachLeadsByAdSchema),
    totals: z.object({ leads: z.number().int().min(0), lastSevenDays: z.number().int().min(0) }).strict(),
    /** False when the workspace has never received a lead — the page then explains how leads arrive. */
    hasLeadsDataset: z.boolean(),
  })
  .strict();
export type ReachLeadsResponse = z.infer<typeof reachLeadsResponseSchema>;

/** `POST /reach/leads/import` — a Meta lead export CSV, through the normal import, into the system dataset. */
export const reachLeadsImportResponseSchema = z
  .object({
    datasetId: uuidSchema,
    imported: z.number().int().min(0),
    rejected: z.number().int().min(0),
    /** People who were not in the workspace before. */
    newPeople: z.number().int().min(0),
    /**
     * Of `imported`, leads older than the API's freshness window (72 hours, by
     * their own `created_time`) or with no readable time: landed as people, but
     * no "new lead" flow starts for them — an export is history.
     */
    historical: z.number().int().min(0),
  })
  .strict();
export type ReachLeadsImportResponse = z.infer<typeof reachLeadsImportResponseSchema>;
